// frontend/src/pages/UserProfilePage.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { zonesApi } from '../api/zones';
import { serviceOpsApi } from '../api/serviceOps';
import { settingsApi } from '../api/settings'; 
import { bulkApi } from '../api/bulk';
import uploadService from '../services/upload';
import { compressImage } from '../utils/imageCompression';
import { 
  FaUser, FaCar, FaBox, FaSignOutAlt, FaSave, FaHeart, FaWarehouse, 
  FaTools, FaBoxOpen, FaHandshake, FaCheckCircle, 
  FaBriefcase, FaInfoCircle, FaTruck, FaTicketAlt, FaBoxes, FaShareAlt, 
  FaSpinner, FaFileContract, FaWallet, FaMapMarkerAlt, FaCamera, FaIdCard, 
  FaClipboardList, FaPlus, FaTimesCircle, FaRedo, FaBars, FaTimes, FaCogs, FaSync, FaCloudUploadAlt
} from 'react-icons/fa';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';

// Components
import MyListings from '../components/profile/MyListings';
import SavedItemsList from '../components/profile/SavedItemsList';
import MyGarage from '../components/profile/MyGarage';
import ServiceHistory from '../components/profile/ServiceHistory';
import MyStoreInventory from '../components/profile/MyStoreInventory';
import ProviderDashboard from '../components/profile/ProviderDashboard';
import FleetManager from '../components/profile/FleetManager'; 
import SupportTicketList from '../components/profile/SupportTicketList';
import BulkRequestManager from '../components/profile/BulkRequestManager';
import ReferralManager from '../components/profile/ReferralManager';
import LogisticsDashboard from '../components/profile/LogisticsDashboard';
import WalletDashboard from '../components/profile/WalletDashboard';
import StoreOrders from '../components/profile/StoreOrders'; 
import AccountSettings from '../components/profile/AccountSettings';
import CameraModal from '../components/common/CameraModal';

// --- CONFIGURATION FOR APPLICATION TYPES ---
const PROVIDER_TYPES = {
  station: {
    label: "Service Station",
    description: "A physical workshop with equipment, bays, and a team of mechanics.",
    skillsLabel: "Services Offered",
    skillsPlaceholder: "e.g. Painting, Engine Overhaul, Wheel Alignment",
    businessLabel: "Workshop Name"
  },
  freelancer: {
    label: "Freelance Mechanic",
    description: "A mobile technician who travels to the customer.",
    skillsLabel: "Core Skills",
    skillsPlaceholder: "e.g. Brake Pads, Oil Change, Diagnostics",
    businessLabel: "Display Name / Business Name"
  },
  expert: {
    label: "Auto Expert",
    description: "A consultant providing high-level advice and inspections.",
    skillsLabel: "Areas of Expertise",
    skillsPlaceholder: "e.g. Pre-purchase Inspection, BMW Systems",
    businessLabel: "Consultancy Name"
  },
  parts_seller: {
    label: "Parts Seller",
    description: "A retailer with a physical or online store selling auto parts.",
    skillsLabel: "Main Product Categories",
    skillsPlaceholder: "e.g. Toyota Parts, Tyres, Batteries",
    businessLabel: "Store Name"
  },
  car_dealer: {
    label: "Car Dealer",
    description: "A business entity selling vehicles (New, Tokunbo, or Nigerian Used).",
    skillsLabel: "Vehicle Types Sold",
    skillsPlaceholder: "e.g. Tokunbo Cars, Brand New, Trucks",
    businessLabel: "Dealership Name"
  },
  logistics_partner: { 
    label: "Logistics Partner",
    description: "A company or individual providing delivery services.",
    skillsLabel: "Service Area / Fleet Type",
    skillsPlaceholder: "e.g. Lagos Island, Nationwide Van Delivery",
    businessLabel: "Logistics Company Name"
  }
};

function UserProfilePage() {
  const { user, logout, updateProfile, reloadUser } = useAuth(); 
  const { addToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('profile');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Number on the "Bulk Purchases" tab: quotes the customer has not opened yet.
  const [bulkUnread, setBulkUnread] = useState(0);
  
  // Location Data
  const [lgas, setLgas] = useState([]);
  const [zones, setZones] = useState([]); 
  const [filteredZones, setFilteredZones] = useState([]); 
  const [mediaConfig, setMediaConfig] = useState({}); 
  
  // Profile Form
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    address: '',
    state: '',
    lga_id: '',
    skills: '',
    custom_state: '',
    custom_lga: ''
  });
  
  // Application Form
  const [appData, setAppData] = useState({
      type: 'station',
      business_name: '',
      description: '',
      skills: '',
      years_experience: 0,
      state: '',
      lga_id: '',
      zone_id: '', 
      address: '',
      kyc_photo_url: '' 
  });
  
  const [appStatus, setAppStatus] = useState(null); 
  const [appErrorMessage, setAppErrorMessage] = useState('');
  const [existingApps, setExistingApps] = useState([]); 
  const [showAppForm, setShowAppForm] = useState(false); 
  
  // Verification & Agreement
  const [isAddressVerified, setIsAddressVerified] = useState(false);
  const [verifyingAddress, setVerifyingAddress] = useState(false);
  const [verifiedAddressText, setVerifiedAddressText] = useState('');
  const [verifiedSource, setVerifiedSource] = useState('');
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [agreementText, setAgreementText] = useState('');
  const [agreed, setAgreed] = useState(false);

  // KYC Upload State
  const [kycPreview, setKycPreview] = useState(null);
  const [uploadingKyc, setUploadingKyc] = useState(false);

  // Camera Modal State (Still used for other features like Fleet/Inventory)
  const [isCameraOpen, setIsCameraOpen] = useState(false);

  const [isSaving, setIsSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // --- SMART ROLE CHECKS ---
  const hasApprovedStore = existingApps.some(app => app.status === 'approved' && app.type === 'parts_seller');
  const hasApprovedDealer = existingApps.some(app => app.status === 'approved' && app.type === 'car_dealer');
  const hasApprovedLogistics = existingApps.some(app => app.status === 'approved' && app.type === 'logistics_partner');
  const hasApprovedProvider = existingApps.some(app => app.status === 'approved' && ['station', 'freelancer', 'expert'].includes(app.type));

  const isStoreManager = user?.roles?.includes('store_manager') || hasApprovedStore;
  const isDealer = user?.roles?.includes('dealer') || hasApprovedDealer;
  const isLogisticsManager = user?.roles?.includes('logistics_manager') || hasApprovedLogistics;
  const isTechnician = user?.roles?.includes('technician') || hasApprovedProvider;
  
  const showWallet = true; 

  // --- Handle URL Query Params for Tab Switching ---
  // Load the unread-quote count for the "Bulk Purchases" tab.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    bulkApi.getUnreadCount()
      .then(res => { if (!cancelled) setBulkUnread(Number(res?.count) || 0); })
      .catch(() => { /* badge is a nice-to-have; never block the page */ });
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab) {
      setActiveTab(tab);
    } else {
      setActiveTab('profile'); // Default to profile if no tab specified
    }
  }, [location]);

  useEffect(() => {
    const fetchLocationData = async () => {
      try {
        const [lgasData, zonesData, settingsData] = await Promise.all([
            zonesApi.getAllLgas(),
            zonesApi.getAllZones(),
            settingsApi.getAllSettings()
        ]);
        setLgas(lgasData);
        setZones(zonesData);
        setMediaConfig(settingsData.media_config || {});
      } catch (err) {
        console.error("Failed to load location data", err);
      }
    };
    fetchLocationData();
  }, []);

  useEffect(() => {
      const checkApplications = async () => {
          try {
              const apps = await serviceOpsApi.getMyApplications();
              if (Array.isArray(apps)) {
                  setExistingApps(apps); 
              } else if (apps) {
                  setExistingApps([apps]);
              }
          } catch (e) {
              // Ignore error
          }
      };
      checkApplications();
  }, []);

  // --- SYNC FORM WITH USER DATA ---
  useEffect(() => {
    if (user) {
      const userFullName = user.full_name || user.fullName;
      const displayName = userFullName && userFullName !== 'Valued Customer' ? userFullName : '';
      
      setFormData(prev => ({
        ...prev,
        full_name: displayName, 
        phone: user.phone || '',
        address: user.address || '',
        state: user.state || '',
        lga_id: user.lga_id || '',
        skills: user.skills || '',
        custom_state: user.custom_state || '',
        custom_lga: user.custom_lga || ''
      }));
    }
  }, [user]);

  // Filter Zones when LGA changes (Application Form)
  useEffect(() => {
      if (appData.lga_id) {
          const relevantZones = zones.filter(z => z.lga_id === appData.lga_id && z.is_active);
          setFilteredZones(relevantZones);
      } else {
          setFilteredZones([]);
      }
  }, [appData.lga_id, zones]);

  const groupedLgas = useMemo(() => {
    return lgas.reduce((acc, lga) => {
      if (!lga.state || !lga.lga) return acc;
      const stateKey = lga.state.trim();
      if (!acc[stateKey]) acc[stateKey] = [];
      acc[stateKey].push(lga);
      return acc;
    }, {});
  }, [lgas]);

  const profileAvailableLgas = formData.state ? groupedLgas[formData.state] || [] : [];
  const appAvailableLgas = appData.state ? groupedLgas[appData.state] || [] : [];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // --- SAVE PROFILE HANDLER ---
  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    
    const payload = {
        full_name: formData.full_name,
        phone: formData.phone,
        address: formData.address,
        state: formData.state,
        lga_id: formData.lga_id,
        skills: formData.skills
    };

    const result = await updateProfile(payload);
    
    if (result.success) {
      addToast('Profile updated successfully!', 'success');
    } else {
      addToast(result.error || "Failed to update profile.", 'error');
    }
    setIsSaving(false);
  };

  // --- REFRESH USER HANDLER ---
  const handleRefreshUser = async () => {
      setIsRefreshing(true);
      if (reloadUser) {
          await reloadUser();
          addToast("Profile refreshed. Check for new permissions.", "info");
      }
      setIsRefreshing(false);
  };

  // --- APPLICATION HANDLERS ---
  const handleAppChange = (e) => {
      const { name, value } = e.target;
      setAppData(prev => ({ ...prev, [name]: value }));
      
      // Any change to the location means the old check no longer applies.
      if (['state', 'lga_id', 'zone_id', 'address'].includes(name)) {
          setIsAddressVerified(false);
          setVerifiedAddressText('');
          setVerifiedSource('');
      }
      
      if (name === 'lga_id') {
          setAppData(prev => ({ ...prev, zone_id: '' }));
      }
  };

  const handleVerifyAddress = async () => {
      if (!appData.state || !appData.lga_id || !appData.address.trim()) {
          alert("Please fill in State, LGA, and Address first.");
          return;
      }

      setVerifyingAddress(true);
      try {
          const lgaName = lgas.find(l => l.id === appData.lga_id)?.lga || '';
          const zoneName = zones.find(z => z.id === appData.zone_id)?.zone_name || '';

          // Send the street on its own - the server adds the Zone, LGA and
          // State itself, so they are not repeated in the search.
          const response = await serviceOpsApi.verifyAddress({
              address: appData.address.trim(),
              lga: lgaName,
              zone: zoneName,
              state: appData.state
          });

          if (response.verified) {
              setIsAddressVerified(true);
              setVerifiedAddressText(`${response.found_address} - ${response.match_type}`);
              setVerifiedSource(response.source || '');
          } else {
              alert(response.message || "We could not verify this address. Please check it and try again.");
              setIsAddressVerified(false);
              setVerifiedAddressText('');
              setVerifiedSource('');
          }
      } catch (err) {
          alert(err?.data?.message || "Verification service unavailable. Please try again later.");
      } finally {
          setVerifyingAddress(false);
      }
  };

  // --- NEW: FILE UPLOAD HANDLER FOR KYC ---
  const handleKycUpload = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      setUploadingKyc(true);
      try {
          const compressionQuality = mediaConfig.kyc_compression || 0.5;
          const compressed = await compressImage(file, compressionQuality);
          
          const folderPath = `provider-kyc/${user.id}`;
          const response = await uploadService.uploadImage(compressed, folderPath);
          
          setAppData(prev => ({ ...prev, kyc_photo_url: response.url }));
          setKycPreview(URL.createObjectURL(compressed));
      } catch (err) {
          alert("Failed to upload document. Please try again.");
      } finally {
          setUploadingKyc(false);
      }
  };

  const handleOpenAgreement = async (e) => {
      e.preventDefault();
      setAppErrorMessage('');

      if (!isAddressVerified) {
          alert("Please verify your business address first.");
          return;
      }
      
      if (filteredZones.length > 0 && !appData.zone_id) {
          alert("Please select a Zone for your business location.");
          return;
      }

      if (!appData.kyc_photo_url) {
          alert("Identity Verification is mandatory. Please upload your ID.");
          return;
      }

      try {
          const res = await serviceOpsApi.getPartnerAgreement();
          setAgreementText(res.content);
          setShowAgreementModal(true);
      } catch (err) {
          alert("Could not load agreement.");
      }
  };

  const handleFinalSubmit = async () => {
      if (!agreed) {
          alert("You must agree to the terms.");
          return;
      }

      setAppStatus('submitting');
      setShowAgreementModal(false);

      try {
          const payload = {
              ...appData,
              skills: appData.skills.split(',').map(s => s.trim()).filter(s => s !== ''),
              years_experience: parseInt(appData.years_experience),
              location_lga_id: appData.lga_id,
              zone_id: appData.zone_id || null 
          };
          
          const response = await serviceOpsApi.submitApplication(payload);
          setAppStatus('success');
          setExistingApps(prev => [...prev, response.application]); 
          setShowAppForm(false); 
          addToast("Application Submitted! You can add another branch if needed.", "success");
      } catch (err) {
          console.error("Application Error:", err);
          setAppStatus('error');
          
          let msg = err.data?.message || err.message || "Failed to submit application.";
          if (err.data?.errors && Array.isArray(err.data.errors)) {
              const details = err.data.errors.map(e => {
                  const field = e.loc[e.loc.length - 1]; 
                  return `${field}: ${e.msg}`;
              }).join(', ');
              msg = `Validation Failed: ${details}`;
          }
          setAppErrorMessage(msg);
          addToast(msg, "error");
      }
  };

  const handleReapply = () => {
      if (window.confirm("Start a new application?")) {
          setShowAppForm(true);
          setAppStatus(null);
          setAppErrorMessage('');
          setKycPreview(null);
          setAppData(prev => ({ ...prev, kyc_photo_url: '' }));
      }
  };

  const handleNavClick = (tab) => {
      setActiveTab(tab);
      setIsMobileMenuOpen(false); 
      // Update URL without reloading
      navigate(`/account?tab=${tab}`, { replace: true });
  };

  const userFullName = user?.full_name || user?.fullName;
  const displayName = userFullName && userFullName !== 'Valued Customer' ? userFullName : 'Valued Customer';
  const displayEmail = user?.email;
  const isPhoneUser = displayEmail?.endsWith('@phone.user');
  const displayContact = isPhoneUser ? user?.phone : displayEmail;

  const currentTypeConfig = PROVIDER_TYPES[appData.type] || PROVIDER_TYPES['station'];

  return (
    <div className="bg-gray-50 min-h-screen py-8 md:py-12">
      <div className="container mx-auto px-4 max-w-6xl">
        
        {/* --- MOBILE HEADER (HAMBURGER) --- */}
        <div className="md:hidden flex justify-between items-center mb-6 bg-white p-4 rounded-lg shadow-sm">
            <h1 className="text-xl font-bold text-gray-800">My Account</h1>
            <button 
                onClick={() => setIsMobileMenuOpen(true)}
                className="relative text-gray-600 hover:text-primary p-2 border rounded"
                aria-label={bulkUnread > 0 ? `Menu, ${bulkUnread} unread` : 'Menu'}
            >
                <FaBars size={20} />
                {bulkUnread > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                    {bulkUnread > 9 ? '9+' : bulkUnread}
                  </span>
                )}
            </button>
        </div>

        {/* --- DESKTOP HEADER --- */}
        <h1 className="hidden md:block text-3xl font-bold text-gray-800 mb-8">My Account</h1>

        <div className="flex flex-col md:flex-row gap-8 relative">
          
          {/* --- MOBILE OVERLAY --- */}
          {isMobileMenuOpen && (
              <div 
                  className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
                  onClick={() => setIsMobileMenuOpen(false)}
              ></div>
          )}

          {/* --- SIDEBAR NAVIGATION --- */}
          <div className={`
              fixed inset-y-0 left-0 z-50 w-72 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out 
              md:translate-x-0 md:static md:w-1/4 md:shadow-none md:bg-transparent md:z-auto
              ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          `}>
            <div className="bg-white rounded-lg shadow-sm overflow-hidden h-full md:h-auto overflow-y-auto">
              
              {/* Mobile Close Button */}
              <div className="md:hidden p-4 flex justify-end border-b">
                  <button onClick={() => setIsMobileMenuOpen(false)} className="text-gray-500">
                      <FaTimes size={24} />
                  </button>
              </div>

              <div className="p-6 border-b border-gray-100 text-center">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-3">
                  {displayName.charAt(0).toUpperCase()}
                </div>
                <h3 className="font-bold text-gray-800">{displayName}</h3>
                <p className="text-xs text-gray-500">{displayContact}</p>
                
                {/* Role Badges */}
                <div className="flex flex-wrap justify-center gap-1 mt-2">
                    {isStoreManager && <span className="bg-green-100 text-green-800 text-[10px] px-2 py-1 rounded-full font-bold">Store Manager</span>}
                    {isDealer && <span className="bg-red-100 text-red-800 text-[10px] px-2 py-1 rounded-full font-bold">Car Dealer</span>}
                    {isLogisticsManager && <span className="bg-yellow-100 text-yellow-800 text-[10px] px-2 py-1 rounded-full font-bold">Logistics</span>}
                    {isTechnician && <span className="bg-blue-100 text-blue-800 text-[10px] px-2 py-1 rounded-full font-bold">Technician</span>}
                </div>

                {/* Refresh Button */}
                <button 
                    onClick={handleRefreshUser}
                    disabled={isRefreshing}
                    className="mt-3 text-xs text-primary hover:underline flex items-center justify-center gap-1 w-full"
                >
                    <FaSync className={isRefreshing ? "animate-spin" : ""} /> Refresh Profile
                </button>
              </div>
              
              <nav className="flex flex-col pb-20 md:pb-0">
                <button 
                  onClick={() => handleNavClick('profile')}
                  className={`flex items-center px-6 py-4 text-sm font-medium transition-colors ${activeTab === 'profile' ? 'bg-blue-50 text-primary border-l-4 border-primary' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  <FaUser className="mr-3" /> Profile Settings
                </button>

                <button 
                  onClick={() => handleNavClick('settings')}
                  className={`flex items-center px-6 py-4 text-sm font-medium transition-colors ${activeTab === 'settings' ? 'bg-blue-50 text-primary border-l-4 border-primary' : 'text-gray-600 hover:bg-gray-50'}`}
                >
                  <FaCogs className="mr-3" /> Security & Settings
                </button>

                {showWallet && (
                    <button 
                      onClick={() => handleNavClick('wallet')}
                      className={`flex items-center px-6 py-4 text-sm font-medium transition-colors ${activeTab === 'wallet' ? 'bg-blue-50 text-primary border-l-4 border-primary' : 'text-gray-600 hover:bg-gray-50'}`}
                    >
                      <FaWallet className="mr-3" /> My Wallet
                    </button>
                )}

                {/* --- PARTNER PORTAL SECTION --- */}
                {(isStoreManager || isDealer || isLogisticsManager || isTechnician) && (
                    <div className="mt-2 pt-2 border-t border-gray-100">
                        <p className="px-6 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider">Partner Portal</p>
                        
                        {isStoreManager && (
                            <>
                                <button 
                                  onClick={() => handleNavClick('shop')}
                                  className={`flex items-center px-6 py-3 text-sm font-medium transition-colors ${activeTab === 'shop' ? 'bg-blue-50 text-primary border-l-4 border-primary' : 'text-gray-600 hover:bg-gray-50'}`}
                                >
                                  <FaBoxOpen className="mr-3 text-green-600" /> My Shop Inventory
                                </button>
                                <button 
                                  onClick={() => handleNavClick('store_orders')}
                                  className={`flex items-center px-6 py-3 text-sm font-medium transition-colors ${activeTab === 'store_orders' ? 'bg-blue-50 text-primary border-l-4 border-primary' : 'text-gray-600 hover:bg-gray-50'}`}
                                >
                                  <FaClipboardList className="mr-3 text-green-600" /> Store Orders
                                </button>
                            </>
                        )}

                        {isDealer && (
                            <button 
                              onClick={() => handleNavClick('listings')}
                              className={`flex items-center px-6 py-3 text-sm font-medium transition-colors ${activeTab === 'listings' ? 'bg-blue-50 text-primary border-l-4 border-primary' : 'text-gray-600 hover:bg-gray-50'}`}
                            >
                              <FaCar className="mr-3 text-red-600" /> Vehicles for Sale
                            </button>
                        )}

                        {isLogisticsManager && (
                            <button 
                              onClick={() => handleNavClick('logistics')}
                              className={`flex items-center px-6 py-3 text-sm font-medium transition-colors ${activeTab === 'logistics' ? 'bg-blue-50 text-primary border-l-4 border-primary' : 'text-gray-600 hover:bg-gray-50'}`}
                            >
                              <FaTruck className="mr-3 text-yellow-600" /> Logistics Dashboard
                            </button>
                        )}

                        {(isTechnician || existingApps.some(a => a.status === 'approved' && ['station', 'freelancer', 'expert'].includes(a.type))) && (
                            <button 
                              onClick={() => handleNavClick('provider_dashboard')}
                              className={`flex items-center px-6 py-3 text-sm font-medium transition-colors ${activeTab === 'provider_dashboard' ? 'bg-blue-50 text-primary border-l-4 border-primary' : 'text-gray-600 hover:bg-gray-50'}`}
                            >
                              <FaBriefcase className="mr-3 text-blue-600" /> Provider Dashboard
                            </button>
                        )}
                    </div>
                )}
                {/* ------------------------------ */}

                <div className="mt-2 pt-2 border-t border-gray-100">
                    <p className="px-6 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider">My Activity</p>
                    
                    <button 
                      onClick={() => handleNavClick('garage')}
                      className={`flex items-center px-6 py-3 text-sm font-medium transition-colors ${activeTab === 'garage' ? 'bg-blue-50 text-primary border-l-4 border-primary' : 'text-gray-600 hover:bg-gray-50'}`}
                    >
                      <FaWarehouse className="mr-3" /> My Garage
                    </button>

                    <button 
                      onClick={() => handleNavClick('services')}
                      className={`flex items-center px-6 py-3 text-sm font-medium transition-colors ${activeTab === 'services' ? 'bg-blue-50 text-primary border-l-4 border-primary' : 'text-gray-600 hover:bg-gray-50'}`}
                    >
                      <FaTools className="mr-3" /> Service History
                    </button>

                    {/* Private Sellers also use Listings, but if they are NOT dealers, show it here */}
                    {!isDealer && (
                        <button 
                          onClick={() => handleNavClick('listings')}
                          className={`flex items-center px-6 py-3 text-sm font-medium transition-colors ${activeTab === 'listings' ? 'bg-blue-50 text-primary border-l-4 border-primary' : 'text-gray-600 hover:bg-gray-50'}`}
                        >
                          <FaCar className="mr-3" /> Vehicles for Sale
                        </button>
                    )}

                    <button 
                      onClick={() => handleNavClick('fleet')}
                      className={`flex items-center px-6 py-3 text-sm font-medium transition-colors ${activeTab === 'fleet' ? 'bg-blue-50 text-primary border-l-4 border-primary' : 'text-gray-600 hover:bg-gray-50'}`}
                    >
                      <FaTruck className="mr-3" /> Fleet Management
                    </button>

                    <button 
                      onClick={() => handleNavClick('bulk')}
                      className={`flex items-center px-6 py-3 text-sm font-medium transition-colors ${activeTab === 'bulk' ? 'bg-blue-50 text-primary border-l-4 border-primary' : 'text-gray-600 hover:bg-gray-50'}`}
                    >
                      <FaBoxes className="mr-3" /> Bulk Purchases
                      {bulkUnread > 0 && (
                        <span
                          className="ml-auto min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center"
                          title={`${bulkUnread} unread`}
                        >
                          {bulkUnread > 99 ? '99+' : bulkUnread}
                        </span>
                      )}
                    </button>

                    <button 
                      onClick={() => handleNavClick('saved')}
                      className={`flex items-center px-6 py-3 text-sm font-medium transition-colors ${activeTab === 'saved' ? 'bg-blue-50 text-primary border-l-4 border-primary' : 'text-gray-600 hover:bg-gray-50'}`}
                    >
                      <FaHeart className="mr-3" /> Saved Items
                    </button>

                    <button 
                      onClick={() => handleNavClick('support')}
                      className={`flex items-center px-6 py-3 text-sm font-medium transition-colors ${activeTab === 'support' ? 'bg-blue-50 text-primary border-l-4 border-primary' : 'text-gray-600 hover:bg-gray-50'}`}
                    >
                      <FaTicketAlt className="mr-3" /> Support Tickets
                    </button>

                    <button 
                      onClick={() => handleNavClick('referrals')}
                      className={`flex items-center px-6 py-3 text-sm font-medium transition-colors ${activeTab === 'referrals' ? 'bg-blue-50 text-primary border-l-4 border-primary' : 'text-gray-600 hover:bg-gray-50'}`}
                    >
                      <FaShareAlt className="mr-3" /> Referral Program
                    </button>

                    <button 
                      onClick={() => handleNavClick('apply')}
                      className={`flex items-center px-6 py-3 text-sm font-medium transition-colors ${activeTab === 'apply' ? 'bg-blue-50 text-primary border-l-4 border-primary' : 'text-gray-600 hover:bg-gray-50'}`}
                    >
                      <FaHandshake className="mr-3" /> Partner with Us
                    </button>

                    <Link 
                      to="/account/orders"
                      className="flex items-center px-6 py-3 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <FaBox className="mr-3" /> Order History
                    </Link>
                </div>

                <button 
                  onClick={logout}
                  className="flex items-center px-6 py-4 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors border-t border-gray-100 mt-auto"
                >
                  <FaSignOutAlt className="mr-3" /> Logout
                </button>
              </nav>
            </div>
          </div>

          {/* MAIN CONTENT AREA */}
          <div className="w-full md:w-3/4">
            
            {/* WELCOME BANNER (NEW) */}
            <div className="bg-white p-4 rounded-lg shadow-sm mb-6 border-l-4 border-primary flex justify-between items-center">
                <div>
                    <h2 className="text-lg font-bold text-gray-800">Welcome, {displayName}</h2>
                    <p className="text-sm text-gray-500">Manage your account, orders, and services.</p>
                </div>
                <div className="hidden md:block text-right">
                    <p className="text-xs text-gray-400 uppercase font-bold">Current Section</p>
                    <p className="text-primary font-bold capitalize">{activeTab.replace('_', ' ')}</p>
                </div>
            </div>

            {/* TAB 1: PROFILE SETTINGS */}
            {activeTab === 'profile' && (
              <div className="bg-white rounded-lg shadow-sm p-6 md:p-8">
                <h2 className="text-xl font-bold text-gray-800 mb-6">Profile Settings</h2>
                
                <form onSubmit={handleSaveProfile}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                      <input 
                        type="text" name="full_name" 
                        value={formData.full_name} onChange={handleChange}
                        placeholder="Your Name"
                        className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-primary focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                      <input 
                        type="tel" name="phone" 
                        value={formData.phone} onChange={handleChange}
                        placeholder="Required to sell cars"
                        className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-primary focus:outline-none"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Personal Address</label>
                      <input 
                        type="text" name="address" 
                        value={formData.address} onChange={handleChange}
                        placeholder="Your home address for deliveries"
                        className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-primary focus:outline-none"
                      />
                    </div>

                    {/* --- DYNAMIC LOCATION SECTION --- */}
                    <div className="md:col-span-2 bg-gray-50 p-4 rounded border border-gray-200">
                        <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                            <FaMapMarkerAlt /> Location Details
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">State</label>
                                <select 
                                    name="state" 
                                    value={formData.state} 
                                    onChange={handleChange} 
                                    className="w-full px-3 py-2 border rounded bg-white focus:outline-none focus:border-primary"
                                >
                                    <option value="">Select State</option>
                                    {Object.keys(groupedLgas).sort().map(state => (
                                        <option key={state} value={state}>{state}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">LGA</label>
                                <select 
                                    name="lga_id" 
                                    value={formData.lga_id} 
                                    onChange={handleChange} 
                                    disabled={!formData.state}
                                    className="w-full px-3 py-2 border rounded bg-white focus:outline-none focus:border-primary disabled:bg-gray-100"
                                >
                                    <option value="">Select LGA</option>
                                    {profileAvailableLgas.map(lga => (
                                        <option key={lga.id} value={lga.id}>{lga.lga}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>
                    {/* -------------------------------- */}

                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Skills / Expertise (Optional)</label>
                        <input 
                            type="text" name="skills" 
                            value={formData.skills} onChange={handleChange}
                            placeholder="e.g. BMW Engine Repair, Car Painting, Hybrid Systems (Comma separated)"
                            className="w-full px-4 py-2 border rounded focus:ring-2 focus:ring-primary focus:outline-none"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                            If you apply to be a partner, these skills will be automatically used for your application.
                        </p>
                    </div>

                  </div>

                  <button 
                    type="submit" 
                    disabled={isSaving}
                    className="bg-primary hover:bg-primary-dark text-white font-bold py-2 px-6 rounded transition duration-300 flex items-center gap-2"
                  >
                    {isSaving ? 'Saving...' : <><FaSave /> Save Changes</>}
                  </button>
                </form>

                {/* --- PARTNER CTA --- */}
                <div className="mt-10 pt-8 border-t border-gray-200">
                    <div className="bg-blue-50 p-6 rounded-lg border border-blue-100 text-center">
                        <h3 className="text-lg font-bold text-blue-800 mb-2">Want to grow your business?</h3>
                        <p className="text-blue-700 mb-4 text-sm">
                            Partner with us as a Service Station, Mechanic, Parts Seller, or Logistics Provider.
                        </p>
                        <button 
                            onClick={() => handleNavClick('apply')}
                            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-full transition duration-300 shadow-md"
                        >
                            Become a Partner
                        </button>
                    </div>
                </div>
              </div>
            )}

            {/* TAB: SETTINGS */}
            {activeTab === 'settings' && (
                <div className="bg-white rounded-lg shadow-sm p-6 md:p-8">
                    <h2 className="text-xl font-bold text-gray-800 mb-6">Security & Account Settings</h2>
                    <AccountSettings />
                </div>
            )}

            {/* TAB: WALLET */}
            {activeTab === 'wallet' && (
                <div className="bg-white rounded-lg shadow-sm p-6 md:p-8">
                    <WalletDashboard />
                </div>
            )}

            {/* TAB: STORE ORDERS */}
            {activeTab === 'store_orders' && (
                <div className="bg-white rounded-lg shadow-sm p-6 md:p-8">
                    <StoreOrders />
                </div>
            )}

            {/* TAB: LOGISTICS DASHBOARD */}
            {activeTab === 'logistics' && (
                <div className="bg-white rounded-lg shadow-sm p-6 md:p-8">
                    <LogisticsDashboard />
                </div>
            )}

            {/* TAB: SHOP INVENTORY */}
            {activeTab === 'shop' && (
                <div className="bg-white rounded-lg shadow-sm p-6 md:p-8">
                    <MyStoreInventory />
                </div>
            )}

            {/* TAB: PROVIDER DASHBOARD */}
            {activeTab === 'provider_dashboard' && <div className="bg-white rounded-lg shadow-sm p-6 md:p-8"><ProviderDashboard /></div>}
            {/* TAB: FLEET */}
            {activeTab === 'fleet' && <div className="bg-white rounded-lg shadow-sm p-6 md:p-8"><FleetManager /></div>}
            {/* TAB: BULK */}
            {activeTab === 'bulk' && <div className="bg-white rounded-lg shadow-sm p-6 md:p-8"><BulkRequestManager onUnreadChange={setBulkUnread} /></div>}
            {/* TAB: SUPPORT */}
            {activeTab === 'support' && <div className="bg-white rounded-lg shadow-sm p-6 md:p-8"><SupportTicketList /></div>}
            {/* TAB: REFERRALS */}
            {activeTab === 'referrals' && <div className="bg-white rounded-lg shadow-sm p-6 md:p-8"><ReferralManager /></div>}
            {/* TAB: GARAGE */}
            {activeTab === 'garage' && <div className="bg-white rounded-lg shadow-sm p-6 md:p-8"><MyGarage /></div>}
            {/* TAB: SERVICES */}
            {activeTab === 'services' && <div className="bg-white rounded-lg shadow-sm p-6 md:p-8"><h2 className="text-xl font-bold text-gray-800 mb-6">Service History</h2><ServiceHistory /></div>}
            {/* TAB: LISTINGS */}
            {activeTab === 'listings' && <div className="bg-white rounded-lg shadow-sm p-6 md:p-8"><MyListings /></div>}
            {/* TAB: SAVED */}
            {activeTab === 'saved' && <div className="bg-white rounded-lg shadow-sm p-6 md:p-8"><h2 className="text-xl font-bold text-gray-800 mb-6">Saved Items</h2><SavedItemsList /></div>}

            {/* TAB: APPLY */}
            {activeTab === 'apply' && (
                <div className="bg-white rounded-lg shadow-sm p-6 md:p-8">
                    <h2 className="text-xl font-bold text-gray-800 mb-4">Partner with Everything Automotive</h2>
                    
                    {/* ... (Toolkit & Existing Apps List) ... */}
                    {existingApps.length > 0 && (
                        <div className="mb-8 space-y-4">
                            <h3 className="font-bold text-gray-700">Your Registered Businesses</h3>
                            {existingApps.map((app, idx) => (
                                <div key={idx} className={`p-4 rounded-lg border flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 ${
                                    app.status === 'approved' ? 'bg-green-50 border-green-200' : 
                                    app.status === 'rejected' ? 'bg-red-50 border-red-200' : 
                                    'bg-yellow-50 border-yellow-200'
                                }`}>
                                    <div className="mb-2 sm:mb-0">
                                        <h4 className="font-bold text-gray-800">{app.business_name}</h4>
                                        <p className="text-xs text-gray-600 capitalize">{app.type.replace('_', ' ')}</p>
                                    </div>
                                    <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-end">
                                        <span className={`text-xs font-bold px-2 py-1 rounded uppercase ${
                                            app.status === 'approved' ? 'bg-green-200 text-green-800' : 
                                            app.status === 'rejected' ? 'bg-red-200 text-red-800' : 
                                            'bg-yellow-200 text-yellow-800'
                                        }`}>
                                            {app.status}
                                        </span>
                                        {app.status === 'rejected' && (
                                            <button 
                                                onClick={handleReapply}
                                                className="text-xs bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700 flex items-center gap-1"
                                            >
                                                <FaRedo /> Retry
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                            
                            {!showAppForm && (
                                <button 
                                    onClick={() => setShowAppForm(true)}
                                    className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-500 font-bold hover:border-primary hover:text-primary transition-colors flex items-center justify-center gap-2"
                                >
                                    <FaPlus /> Add Another Branch / Business
                                </button>
                            )}
                        </div>
                    )}

                    {/* --- APPLICATION FORM --- */}
                    {(existingApps.length === 0 || showAppForm) && (
                        <form onSubmit={handleOpenAgreement} className="space-y-6 animate-fadeInUp">
                            
                            {/* ... (Type Selection & Business Details) ... */}
                            <div>
                                <label className="block text-sm font-bold text-gray-700 mb-2">I want to register as:</label>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                    {Object.keys(PROVIDER_TYPES).map(type => (
                                        <label key={type} className={`border rounded p-3 cursor-pointer flex flex-col items-center gap-2 transition-all ${appData.type === type ? 'border-primary bg-blue-50 text-primary ring-2 ring-primary ring-opacity-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                                            <input type="radio" name="type" value={type} checked={appData.type === type} onChange={handleAppChange} className="hidden" />
                                            <span className="capitalize font-bold text-center text-sm">{PROVIDER_TYPES[type].label}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{currentTypeConfig.businessLabel}</label>
                                    <input name="business_name" value={appData.business_name} onChange={handleAppChange} required placeholder={currentTypeConfig.placeholder_business || "Enter Name"} className="w-full p-2 border rounded focus:ring-2 focus:ring-primary focus:outline-none" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Years of Experience</label>
                                    <input type="number" name="years_experience" value={appData.years_experience} onChange={handleAppChange} required className="w-full p-2 border rounded focus:ring-2 focus:ring-primary focus:outline-none" />
                                </div>
                            </div>

                            {/* Location & Verification */}
                            <div className="bg-gray-50 p-4 rounded border border-gray-200">
                                <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2"><FaMapMarkerAlt /> Business Location (Primary)</h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">State</label>
                                        <select name="state" value={appData.state} onChange={handleAppChange} required className="w-full px-3 py-2 border rounded bg-white focus:outline-none focus:border-primary">
                                            <option value="">Select State</option>
                                            {Object.keys(groupedLgas).sort().map(state => (
                                                <option key={state} value={state}>{state}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">LGA</label>
                                        <select name="lga_id" value={appData.lga_id} onChange={handleAppChange} required disabled={!appData.state} className="w-full px-3 py-2 border rounded bg-white focus:outline-none focus:border-primary disabled:bg-gray-100">
                                            <option value="">Select LGA</option>
                                            {appAvailableLgas.map(lga => (<option key={lga.id} value={lga.id}>{lga.lga}</option>))}
                                        </select>
                                    </div>
                                    {/* --- ZONE DROPDOWN --- */}
                                    <div>
                                        <label className="block text-xs font-medium text-gray-600 mb-1">Zone (Required)</label>
                                        <select 
                                            name="zone_id" 
                                            value={appData.zone_id} 
                                            onChange={handleAppChange} 
                                            required 
                                            disabled={!appData.lga_id} 
                                            className="w-full px-3 py-2 border rounded bg-white focus:outline-none focus:border-primary disabled:bg-gray-100"
                                        >
                                            <option value="">Select Zone</option>
                                            {filteredZones.length > 0 ? (
                                                filteredZones.map(zone => (
                                                    <option key={zone.id} value={zone.id}>{zone.zone_name}</option>
                                                ))
                                            ) : (
                                                <option value="" disabled>No zones found for this LGA</option>
                                            )}
                                        </select>
                                        {appData.lga_id && filteredZones.length === 0 && (
                                            <p className="text-[10px] text-red-500 mt-1">
                                                No zones defined. Please contact admin or select a different LGA.
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="mb-2">
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Street Address</label>
                                    <div className="flex flex-col sm:flex-row gap-2">
                                        <input name="address" value={appData.address} onChange={handleAppChange} required placeholder="e.g. 5 Adejuwon Street" className="w-full sm:flex-grow px-3 py-2 border rounded focus:outline-none focus:border-primary" />
                                        <button type="button" onClick={handleVerifyAddress} disabled={verifyingAddress || isAddressVerified} className={`w-full sm:w-auto px-4 py-2 rounded text-sm font-bold transition-colors ${isAddressVerified ? 'bg-green-100 text-green-700 cursor-default' : 'bg-blue-600 text-white hover:bg-blue-700'}`}>
                                            {verifyingAddress ? <FaSpinner className="animate-spin mx-auto" /> : isAddressVerified ? 'Verified' : 'Verify'}
                                        </button>
                                    </div>
                                </div>
                                {isAddressVerified && (
                                    <div className="text-green-600 text-xs font-bold flex items-center gap-1 animate-fadeInUp mt-2 bg-green-50 p-2 rounded border border-green-200">
                                        <FaCheckCircle className="flex-shrink-0" /> <span>Location verified{verifiedSource ? ` via ${verifiedSource}` : ''}: {verifiedAddressText}</span>
                                    </div>
                                )}
                            </div>

                            {/* --- MANDATORY KYC SECTION (UPDATED) --- */}
                            <div className="bg-yellow-50 p-4 rounded border border-yellow-200">
                                <h3 className="text-sm font-bold text-yellow-800 mb-3 flex items-center gap-2">
                                    <FaIdCard /> Identity Verification (Mandatory)
                                </h3>
                                <p className="text-xs text-yellow-700 mb-3">
                                    To ensure trust and safety, we require a clear photo of your Valid Government ID or Passport. This will be used for internal verification only.
                                </p>
                                
                                <div className="flex flex-col items-center justify-center border-2 border-dashed border-yellow-300 rounded-lg p-4 bg-white max-w-sm mx-auto">
                                    {uploadingKyc ? (
                                        <div className="text-yellow-600 flex flex-col items-center">
                                            <FaSpinner className="animate-spin text-2xl mb-2" />
                                            <span>Uploading secure document...</span>
                                        </div>
                                    ) : kycPreview ? (
                                        <div className="relative">
                                            <img src={kycPreview} alt="KYC Preview" className="w-full h-auto object-contain rounded border-4 border-yellow-400" />
                                            <button 
                                                type="button"
                                                onClick={() => { setKycPreview(null); setAppData(prev => ({...prev, kyc_photo_url: ''})); }}
                                                className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600"
                                            >
                                                <FaTimesCircle />
                                            </button>
                                            <div className="mt-2 text-center text-green-600 font-bold text-xs flex items-center justify-center gap-1">
                                                <FaCheckCircle /> Photo Uploaded
                                            </div>
                                        </div>
                                    ) : (
                                        <label className="cursor-pointer bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-2 px-6 rounded-full flex items-center gap-2 shadow-md transition-transform hover:scale-105">
                                            <FaCloudUploadAlt /> Upload Passport / ID
                                            <input 
                                                type="file" 
                                                accept="image/*" 
                                                onChange={handleKycUpload} 
                                                className="hidden" 
                                            />
                                        </label>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{currentTypeConfig.skillsLabel} (Comma separated)</label>
                                <input name="skills" value={appData.skills} onChange={handleAppChange} required placeholder={currentTypeConfig.skillsPlaceholder} className="w-full p-2 border rounded focus:ring-2 focus:ring-primary focus:outline-none" />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description / Bio</label>
                                <textarea 
                                    name="description" 
                                    value={appData.description} 
                                    onChange={handleAppChange} 
                                    required 
                                    rows="4" 
                                    minLength={10}
                                    placeholder="Tell us about your services and expertise (Min 10 chars)..." 
                                    className="w-full p-2 border rounded focus:ring-2 focus:ring-primary focus:outline-none"
                                ></textarea>
                            </div>

                            <div className="flex gap-3">
                                {showAppForm && (
                                    <button 
                                        type="button" 
                                        onClick={() => setShowAppForm(false)}
                                        className="flex-1 bg-gray-200 text-gray-700 font-bold py-3 px-4 rounded transition duration-300"
                                    >
                                        Cancel
                                    </button>
                                )}
                                <button type="submit" disabled={appStatus === 'submitting'} className="flex-1 bg-primary hover:bg-primary-dark text-white font-bold py-3 px-4 rounded transition duration-300">
                                    Next Step
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            )}

            {/* ... (Modals) ... */}
            {/* --- AGREEMENT MODAL --- */}
            {showAgreementModal && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
                    <div className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
                        <div className="flex justify-between items-center p-6 border-b">
                            <h3 className="text-xl font-bold flex items-center gap-2"><FaFileContract className="text-primary" /> Partner Agreement</h3>
                            <button onClick={() => setShowAgreementModal(false)}><FaTimesCircle className="text-gray-400 hover:text-gray-600 text-xl" /></button>
                        </div>
                        
                        <div className="p-6 overflow-y-auto bg-gray-50 text-sm text-gray-700 border-b max-h-[60vh]">
                            <ReactMarkdown 
                                components={{
                                    h1: ({node, ...props}) => <h1 className="text-2xl font-bold text-gray-900 mt-6 mb-3" {...props}>{props.children}</h1>,
                                    h2: ({node, ...props}) => <h2 className="text-xl font-bold text-gray-800 mt-5 mb-2" {...props}>{props.children}</h2>,
                                    h3: ({node, ...props}) => <h3 className="text-lg font-bold text-gray-800 mt-4 mb-2" {...props}>{props.children}</h3>,
                                    p: ({node, ...props}) => <p className="mb-3 leading-relaxed text-justify" {...props}>{props.children}</p>,
                                    ul: ({node, ...props}) => <ul className="list-disc list-inside mb-3 pl-4" {...props}>{props.children}</ul>,
                                    ol: ({node, ...props}) => <ol className="list-decimal list-inside mb-3 pl-4" {...props}>{props.children}</ol>,
                                    li: ({node, ...props}) => <li className="mb-1 text-justify" {...props}>{props.children}</li>,
                                    strong: ({node, ...props}) => <strong className="font-bold text-gray-900" {...props}>{props.children}</strong>,
                                }}
                            >
                                {agreementText}
                            </ReactMarkdown>
                            
                            <div className="mt-6 p-4 bg-blue-100 text-blue-800 rounded border border-blue-200 font-bold">
                                IMPORTANT: By registering, you agree to ship all orders within 48 hours of payment confirmation. Failure to do so may result in penalties or account suspension.
                            </div>
                        </div>

                        <div className="p-6 bg-white border-t">
                            <label className="flex items-center gap-3 cursor-pointer mb-4">
                                <input 
                                    type="checkbox" 
                                    checked={agreed} 
                                    onChange={(e) => setAgreed(e.target.checked)} 
                                    className="h-5 w-5 text-primary rounded" 
                                />
                                <span className="font-bold text-gray-800">
                                    I have read and agree to the Partner Terms & Conditions above.
                                </span>
                            </label>

                            <div className="flex justify-end gap-3">
                                <button onClick={() => setShowAgreementModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded">Cancel</button>
                                {agreed && (
                                    <button 
                                        onClick={handleFinalSubmit} 
                                        disabled={appStatus === 'submitting'}
                                        className="px-6 py-2 bg-green-600 text-white rounded font-bold hover:bg-green-700 flex items-center gap-2 animate-fadeIn"
                                    >
                                        {appStatus === 'submitting' ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />} Submit Application
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* --- CAMERA MODAL FOR KYC (REMOVED FROM THIS PAGE BUT KEPT FOR OTHER USES) --- */}
            <CameraModal 
                isOpen={isCameraOpen} 
                onClose={() => setIsCameraOpen(false)} 
                onCapture={() => {}} // No-op here as we use file upload
                viewType="face_verification"
            />

          </div>
        </div>
      </div>
    </div>
  );
}

export default UserProfilePage;